import { QuestionCircleOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import classNames from 'classnames'
import { useWatch } from 'react-hook-form'

import type { OptionType } from '@/types'
import { digitsMask } from '@/utils/forms/inputMasks'

import { labelWithTooltip } from '../../RequiredLabel/labelWithTooltip'
import type { ConfigPreviewResponse } from '../../types/stockImports.types'
import {
  ConfigBooleanSelectField,
  type ConfigFieldContext,
  ConfigSelectField,
  ConfigTextField,
} from '../ConfigFields/ConfigFields'
import cn from '../ConfigModal.module.scss'
import type { ConfigFileFormat, ConfigSourceType } from '../lib/configFormModel'
import { toMapperValues } from '../lib/configFormModel'
import { COLUMN_INDEX_HINT, CONFIG_TOOLTIPS } from '../lib/configModalTexts'
import {
  type PriceColumnValues,
  splitPriceMapping,
  toPriceMapperValues,
} from '../lib/configPriceColumns'
import { getSegmentIdsByColumn } from '../lib/configSegmentColumns'
import { formatExpirationDays } from '../lib/expirationDays'
import type { PriceFieldContext } from '../PricesStep/PriceFields'
import PriceSettingsFields from '../PricesStep/PriceSettingsFields'
import UrlExcelMapper from '../UrlExcelMapper/UrlExcelMapper'
import {
  columnRoles,
  type ExcelMapperValues,
  type PriceColumnRole,
  priceColumnRoles,
  priceColumnRolesWithoutCurrency,
} from '../UrlExcelMapper/UrlExcelMapper.types'
import { SegmentColumnsContext } from '../UrlExcelTable/SegmentColumnsContext'
import ConfigBrandFields from './ConfigBrandFields'
import ConfigFileFields from './ConfigFileFields'
import ConfigRulesFields from './ConfigRulesFields'
import QuantityHeaderField from './ImportProtectionFields/QuantityHeaderField'
import { useColumnConflicts } from './useColumnConflicts'

const STOCK_AND_PRICE_ROLES = [...columnRoles, ...priceColumnRoles]
const STOCK_AND_PRICE_ROLES_WITHOUT_CURRENCY = [
  ...columnRoles,
  ...priceColumnRolesWithoutCurrency,
]

interface SettingsStepProps {
  context: ConfigFieldContext
  hidden: boolean
  sourceType: ConfigSourceType
  fileFormat: ConfigFileFormat
  url: string
  cronOptions: OptionType[]
  brandNameOptions: OptionType[]
  brandOptions: OptionType[]
  brandsLoading: boolean
  quantityHeaders: ConfigPreviewResponse['quantityHeaders']
  priceColumns: {
    context: PriceFieldContext
    canEdit: boolean
    visible: boolean
    setSegmentColumn: (columnIndex: number, segmentIds: string[]) => void
  }
  onMappingChange: (values: ExcelMapperValues) => void
  onPriceColumnChange: (field: PriceColumnRole, value: string) => void
  onUseQuantityHeader: (header: string) => void
}

function SettingsStep({
  context,
  hidden,
  sourceType,
  fileFormat,
  url,
  cronOptions,
  brandNameOptions,
  brandOptions,
  brandsLoading,
  quantityHeaders,
  priceColumns,
  onMappingChange,
  onPriceColumnChange,
  onUseQuantityHeader,
}: SettingsStepProps) {
  const { control } = context
  const [
    startRowIndex,
    endRowIndex,
    articleColumnNumber,
    articleInchColumnNumber,
    barcodeColumnNumber,
    descriptionColumnNumber,
    brandColumnNumber,
    quantityColumnNumber,
    expirationHours,
  ] = useWatch({
    control,
    name: [
      'startRowIndex',
      'endRowIndex',
      'articleColumnNumber',
      'articleInchColumnNumber',
      'barcodeColumnNumber',
      'descriptionColumnNumber',
      'brandColumnNumber',
      'quantityColumnNumber',
      'expirationHours',
    ],
  })
  const mapperValues = toMapperValues({
    startRowIndex,
    endRowIndex,
    articleColumnNumber,
    articleInchColumnNumber,
    barcodeColumnNumber,
    descriptionColumnNumber,
    brandColumnNumber,
    quantityColumnNumber,
  })
  const [purchaseColumnNumber, rrcColumnNumber, currencyColumnNumber] =
    useWatch({
      control: priceColumns.context.control,
      name: ['purchaseColumnNumber', 'rrcColumnNumber', 'currencyColumnNumber'],
    })
  const priceColumnValues: PriceColumnValues = {
    purchaseColumnNumber,
    rrcColumnNumber,
    currencyColumnNumber,
  }
  const [currencyFromFile, segments] = useWatch({
    control: priceColumns.context.control,
    name: ['currencyFromFile', 'segments'],
  })
  const segmentColumns = priceColumns.visible
    ? {
        segments: segments.map((row) => ({
          id: row.segmentId,
          label: row.description,
        })),
        segmentIdsByColumn: getSegmentIdsByColumn(segments),
        onChange: priceColumns.setSegmentColumn,
      }
    : null
  const priceContext = priceColumns.visible ? priceColumns.context : undefined
  const columnConflicts = useColumnConflicts(context, priceColumns.context)
  const priceRoles = currencyFromFile
    ? priceColumnRoles
    : priceColumnRolesWithoutCurrency
  const stockAndPriceRoles = currencyFromFile
    ? STOCK_AND_PRICE_ROLES
    : STOCK_AND_PRICE_ROLES_WITHOUT_CURRENCY

  const handleMappingChange = (values: ExcelMapperValues) => {
    const { stock, prices } = splitPriceMapping(values)
    if (Object.keys(stock).length > 0) onMappingChange(stock)
    priceColumnRoles.forEach(({ value }) => {
      const columnNumber = prices[value]
      if (columnNumber !== undefined) onPriceColumnChange(value, columnNumber)
    })
  }

  const isExcel = fileFormat === 'EXCEL'
  const isMail = sourceType === 'EMAIL' || sourceType === 'CUSTOM_IMAP'

  return (
    <div
      className={classNames(cn.step, hidden && cn.stepHidden)}
      aria-hidden={hidden}
    >
      <div className={cn.grid}>
        {sourceType !== 'EMAIL' && (
          <ConfigSelectField
            context={context}
            name="cronExpression"
            title="Как часто проверять"
            required
            placeholder="Выберите интервал"
            options={cronOptions}
          />
        )}
        <ConfigTextField
          context={context}
          name="expirationHours"
          title={labelWithTooltip(
            'Часов до просрочки',
            CONFIG_TOOLTIPS.expirationHours
          )}
          required
          mask={digitsMask}
          trailingText={formatExpirationDays(expirationHours)}
        />
        <ConfigBooleanSelectField
          context={context}
          name="excludeWeekends"
          title="Выходные при расчёте просрочки"
          trueLabel="Исключать"
          falseLabel="Учитывать"
        />
      </div>

      {priceColumns.canEdit && (
        <>
          <p className={cn.sectionTitle}>Цены</p>
          <PriceSettingsFields context={priceColumns.context} />
        </>
      )}

      <p className={cn.sectionTitle}>Бренды</p>
      <ConfigBrandFields
        context={context}
        brandNameOptions={brandNameOptions}
        brandOptions={brandOptions}
        brandsLoading={brandsLoading}
      />

      <p className={cn.sectionTitle}>Остатки</p>
      <div className={cn.grid}>
        <QuantityHeaderField
          context={context}
          quantityHeaders={quantityHeaders}
          onUseQuantityHeader={onUseQuantityHeader}
        />
      </div>

      <p className={cn.sectionTitle}>
        Файл и колонки
        <Tooltip trigger="click" placement="topLeft" title={COLUMN_INDEX_HINT}>
          <QuestionCircleOutlined
            className={cn.helpIcon}
            aria-label="Как указать номер колонки"
          />
        </Tooltip>
      </p>
      {isExcel && (sourceType === 'URL' || isMail) ? (
        <SegmentColumnsContext.Provider value={segmentColumns}>
          <UrlExcelMapper
            values={
              priceContext
                ? { ...mapperValues, ...toPriceMapperValues(priceColumnValues) }
                : mapperValues
            }
            roles={priceContext ? stockAndPriceRoles : columnRoles}
            url={sourceType === 'URL' ? url : undefined}
            disabled={sourceType === 'URL' && !url.trim()}
            localFile={isMail}
            onMappingChange={handleMappingChange}
            renderFields={(isFileLoaded) => (
              <ConfigFileFields
                context={context}
                isFileLoaded={isFileLoaded}
                priceContext={priceContext}
                priceRoles={priceRoles}
                segmentColumns={segmentColumns}
                conflicts={columnConflicts}
                showSheetFields
              />
            )}
          />
        </SegmentColumnsContext.Provider>
      ) : (
        <ConfigFileFields
          context={context}
          isFileLoaded={false}
          priceContext={priceContext}
          priceRoles={priceRoles}
          segmentColumns={segmentColumns}
          conflicts={columnConflicts}
          showSheetFields={isExcel}
        />
      )}

      <ConfigRulesFields context={context} sourceType={sourceType} />
    </div>
  )
}

export default SettingsStep
